import { useState } from 'react';
import TimedNotice from '../../timedNotice.js';
import { TRAIT_SEEDS } from '../../../db/traits.js';
import type { LearnedTrait } from '../../../domain/types.js';
import { displayLabel, traitOptionLabel } from '../../displayLabel.js';
import { MAX_REASON, MAX_TRAITS } from './defaults.js';
import { validateTraitDraft, validateTraits } from './validation.js';

export interface TraitPickerDraft {
  traitKey: string;
  strength: number;
  reason: string;
}

const blankPicker = (): TraitPickerDraft => ({ traitKey: '', strength: 5, reason: '' });

export default function CharacterTraitsScreen(props: {
  traits: LearnedTrait[];
  savedCount: number;
  error: string | null;
  onTraitsChange: (traits: LearnedTrait[]) => void;
  onAiSuggest: () => void;
  onBack: () => void;
  onSaveToast: () => void;
  onReset: () => void;
  onCancel: () => void;
  onSaveAndAdd: () => void;
  onSaveAndContinue: () => void;
  /** Saved character opened from Edit. The end actions save or discard that edit. */
  editing?: boolean;
}): JSX.Element {
  const [picker, setPicker] = useState<TraitPickerDraft>(blankPicker);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const usedKeys = new Set(props.traits.map((t) => t.traitKey));
  const atTraitMax = props.traits.length >= MAX_TRAITS;

  function tryAddTrait(): void {
    const candidate: LearnedTrait = {
      traitKey: picker.traitKey,
      strength: picker.strength,
      reason: picker.reason,
    };
    const err = validateTraitDraft(candidate);
    if (err) {
      setPickerError(err);
      return;
    }
    if (usedKeys.has(picker.traitKey)) {
      setPickerError('No duplicate traits — pick distinct ones.');
      return;
    }
    if (picker.reason.length > MAX_REASON) {
      setPickerError(`Reasons cap at ${MAX_REASON} characters.`);
      return;
    }
    props.onTraitsChange([...props.traits, candidate]);
    setPicker(blankPicker());
    setPickerError(null);
  }

  function tryUpdateTrait(index: number): void {
    const candidate: LearnedTrait = {
      traitKey: picker.traitKey,
      strength: picker.strength,
      reason: picker.reason,
    };
    const err = validateTraitDraft(candidate);
    if (err) {
      setPickerError(err);
      return;
    }
    if (picker.reason.length > MAX_REASON) {
      setPickerError(`Reasons cap at ${MAX_REASON} characters.`);
      return;
    }
    const dup = props.traits.some((t, i) => i !== index && t.traitKey === picker.traitKey);
    if (dup) {
      setPickerError('No duplicate traits — pick distinct ones.');
      return;
    }
    props.onTraitsChange(props.traits.map((t, i) => (i === index ? candidate : t)));
    setEditingIndex(null);
    setPicker(blankPicker());
    setPickerError(null);
  }

  const reasonOverLimit = picker.reason.length > MAX_REASON;
  const recommendAdd = !props.editing && props.savedCount < 2;
  const traitSaveDisabled = validateTraits(props.traits) !== null && props.traits.length > 0;

  function handleReasonChange(raw: string): void {
    setPickerError(raw.length > MAX_REASON ? `Reasons cap at ${MAX_REASON} characters.` : null);
    setPicker((p) => ({ ...p, reason: raw }));
  }

  return (
    <section className="world-char-form" aria-label="Learned traits">
      <h2>Traits</h2>
      <p className="world-step-subtitle">Learned traits ({props.traits.length}/{MAX_TRAITS})</p>

      {props.traits.length > 0 && (
        <ul className="world-char-list" aria-label="Added traits">
          {props.traits.map((t, i) => (
            <li key={`${t.traitKey}-${i}`}>
              <span>{displayLabel(t.traitKey)} · strength {t.strength}/10 · {t.reason}</span>
              <div className="world-action-row">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setEditingIndex(i);
                    setPicker({ traitKey: t.traitKey, strength: t.strength, reason: t.reason });
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => props.onTraitsChange(props.traits.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!atTraitMax && (
        <div className="world-field-row">
          <label htmlFor="trait-pick">Pick a trait</label>
          <select
            id="trait-pick"
            value={picker.traitKey}
            onChange={(e) => setPicker((p) => ({ ...p, traitKey: e.target.value }))}
          >
            <option value="">— pick a trait —</option>
            {TRAIT_SEEDS.filter(([key]) => !usedKeys.has(key) || key === picker.traitKey).map(([key, cat]) => (
              <option key={key} value={key}>{traitOptionLabel(key, cat)}</option>
            ))}
          </select>
          <div className="world-field-label-row">
            <span>Strength</span>
            <span className="world-muted">{picker.strength}/10</span>
          </div>
          <input
            type="range"
            min={0}
            max={10}
            step={1}
            value={picker.strength}
            onChange={(e) => setPicker((p) => ({ ...p, strength: Number(e.target.value) }))}
            aria-label="Trait strength"
          />
          <label htmlFor="trait-reason">Why this trait, did something happen? One line (100 chars max).</label>
          <textarea
            id="trait-reason"
            rows={2}
            value={picker.reason}
            onChange={(e) => handleReasonChange(e.target.value)}
            aria-describedby="trait-reason-count"
          />
          <span
            id="trait-reason-count"
            className={reasonOverLimit ? 'world-error' : 'world-muted'}
          >
            {picker.reason.length}/{MAX_REASON}
          </span>
          {editingIndex === null ? (
            <button
              type="button"
              className="btn-ghost"
              disabled={!picker.traitKey || reasonOverLimit}
              onClick={tryAddTrait}
            >
              + Add trait
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={reasonOverLimit}
              onClick={() => tryUpdateTrait(editingIndex)}
            >
              Save trait edit
            </button>
          )}
        </div>
      )}

      <TimedNotice text={props.error ?? pickerError} className="world-error" role="alert" />

      <div className="world-action-row">
        <button type="button" className="btn-ghost" onClick={props.onAiSuggest}>Let AI fill in the blanks</button>
        {!props.editing && (
          <button type="button" className="btn-ghost" onClick={props.onCancel}>Cancel</button>
        )}
      </div>

      <div className="world-traits-save-row">
        {recommendAdd && (
          <p className="world-muted world-traits-save-hint">Add at least one more — 2–8 suggested</p>
        )}
        {props.editing ? (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={traitSaveDisabled}
              onClick={props.onSaveAndAdd}
            >
              Save character edits
            </button>
            <button type="button" className="btn-ghost" onClick={props.onCancel}>
              Cancel edits and return
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={recommendAdd ? 'btn-primary' : 'btn-ghost'}
              disabled={traitSaveDisabled}
              onClick={props.onSaveAndAdd}
            >
              Save and add character
            </button>
            <button
              type="button"
              className={recommendAdd ? 'btn-ghost' : 'btn-primary'}
              onClick={props.onSaveAndContinue}
            >
              Save and Continue
            </button>
          </>
        )}
      </div>
    </section>
  );
}
